import React from 'react';

interface SortGroupControlsProps {
  sortBy: string;
  groupBy: string;
  onSortChange: (value: string) => void;
  onGroupChange: (value: string) => void;
  type: 'lieux' | 'objets';
  lieuType?: 'sites' | 'batiments' | 'pieces' | 'zones' | 'boxes';
  className?: string;
}

const SortGroupControls: React.FC<SortGroupControlsProps> = ({
  sortBy,
  groupBy,
  onSortChange,
  onGroupChange,
  type,
  lieuType,
  className = '',
}) => {
  // Options de tri personnalisées par type de lieu
  const getSortOptions = () => {
    if (type === 'objets') {
      return [
        { value: 'nom-asc', label: '📝 Nom (A→Z)' },
        { value: 'nom-desc', label: '📝 Nom (Z→A)' },
        { value: 'categorie-asc', label: '📁 Catégorie (A→Z)' },
        { value: 'etat-asc', label: '⚙️ État (A→Z)' },
        { value: 'quantite-desc', label: '🔢 Quantité (↓)' },
        { value: 'recent', label: '🕒 Plus récents' },
      ];
    }

    // Options spécifiques par type de lieu
    switch (lieuType) {
      case 'sites':
        return [
          { value: 'nom-asc', label: '📝 Nom (A→Z)' },
          { value: 'nom-desc', label: '📝 Nom (Z→A)' },
          { value: 'trigram-asc', label: '🔤 Trigramme (A→Z)' },
          { value: 'batiments-desc', label: '🏗️ Nombre de bâtiments (↓)' },
          { value: 'recent', label: '🕒 Plus récents' },
        ];
      
      case 'batiments':
        return [
          { value: 'nom-asc', label: '📝 Nom (A→Z)' },
          { value: 'nom-desc', label: '📝 Nom (Z→A)' },
          { value: 'trigram-asc', label: '🔤 Trigramme (A→Z)' },
          { value: 'etages-desc', label: ' tầng Nombre d\'étages (↓)' },
          { value: 'site-asc', label: '📍 Site (A→Z)' },
          { value: 'pieces-desc', label: '🚪 Nombre de pièces (↓)' },
          { value: 'recent', label: '🕒 Plus récents' },
        ];
      
      case 'pieces':
        return [
          { value: 'nom-asc', label: '📝 Nom (A→Z)' },
          { value: 'nom-desc', label: '📝 Nom (Z→A)' },
          { value: 'numero-asc', label: '🔢 Numéro (A→Z)' },
          { value: 'etage-asc', label: ' tầng Étage (↓)' },
          { value: 'etage-desc', label: ' tầng Étage (↑)' },
          { value: 'batiment-asc', label: '🏗️ Bâtiment (A→Z)' },
          { value: 'objets-desc', label: '📦 Nombre d\'objets (↓)' },
          { value: 'recent', label: '🕒 Plus récents' },
        ];
      
      case 'zones':
        return [
          { value: 'nom-asc', label: '📝 Nom (A→Z)' },
          { value: 'nom-desc', label: '📝 Nom (Z→A)' },
          { value: 'code-asc', label: '🏷️ Code (A→Z)' },
          { value: 'theme-asc', label: '🎨 Thème (A→Z)' },
          { value: 'piece-asc', label: '🚪 Pièce (A→Z)' },
          { value: 'objets-desc', label: '📦 Nombre d\'objets (↓)' },
          { value: 'recent', label: '🕒 Plus récents' },
        ];
      
      case 'boxes':
        return [
          { value: 'nom-asc', label: '📝 Nom (A→Z)' },
          { value: 'nom-desc', label: '📝 Nom (Z→A)' },
          { value: 'code-asc', label: '🏷️ Code (A→Z)' },
          { value: 'theme-asc', label: '🎨 Thème (A→Z)' },
          { value: 'piece-asc', label: '🚪 Pièce (A→Z)' },
          { value: 'objets-desc', label: '📦 Nombre d\'objets (↓)' },
          { value: 'recent', label: '🕒 Plus récents' },
        ];
      
      default:
        return [
          { value: 'nom-asc', label: '📝 Nom (A→Z)' },
          { value: 'nom-desc', label: '📝 Nom (Z→A)' },
          { value: 'recent', label: '🕒 Plus récents' },
        ];
    }
  };

  // Options de groupement personnalisées par type de lieu
  const getGroupOptions = () => {
    if (type === 'objets') {
      return [
        { value: 'none', label: 'Aucun groupement' },
        { value: 'categorie', label: '📁 Par Catégorie' },
        { value: 'sous-categorie', label: '📂 Par Sous-catégorie' },
        { value: 'etat', label: '⚙️ Par État' },
        { value: 'lieu', label: '📍 Par Lieu' },
        { value: 'categorie-etat', label: '📁⚙️ Catégorie + État' },
      ];
    }

    // Options spécifiques par type de lieu
    switch (lieuType) {
      case 'sites':
        return [
          { value: 'none', label: 'Aucun groupement' },
          { value: 'trigram', label: '🔤 Par Trigramme' },
          { value: 'batiments', label: '🏗️ Par Nombre de bâtiments' },
        ];
      
      case 'batiments':
        return [
          { value: 'none', label: 'Aucun groupement' },
          { value: 'site', label: '📍 Par Site' },
          { value: 'etages', label: ' tầng Par Nombre d\'étages' },
          { value: 'pieces', label: '🚪 Par Nombre de pièces' },
          { value: 'site-etages', label: '📍 tầng Site + Étages' },
        ];
      
      case 'pieces':
        return [
          { value: 'none', label: 'Aucun groupement' },
          { value: 'batiment', label: '🏗️ Par Bâtiment' },
          { value: 'etage', label: ' tầng Par Étage' },
          { value: 'objets', label: '📦 Par Nombre d\'objets' },
          { value: 'batiment-etage', label: '🏗️ tầng Bâtiment + Étage' },
        ];
      
      case 'zones':
        return [
          { value: 'none', label: 'Aucun groupement' },
          { value: 'piece', label: '🚪 Par Pièce' },
          { value: 'theme', label: '🎨 Par Thème' },
          { value: 'objets', label: '📦 Par Nombre d\'objets' },
          { value: 'piece-theme', label: '🚪🎨 Pièce + Thème' },
        ];
      
      case 'boxes':
        return [
          { value: 'none', label: 'Aucun groupement' },
          { value: 'piece', label: '🚪 Par Pièce' },
          { value: 'theme', label: '🎨 Par Thème' },
          { value: 'objets', label: '📦 Par Nombre d\'objets' },
          { value: 'piece-theme', label: '🚪🎨 Pièce + Thème' },
        ];
      
      default:
        return [
          { value: 'none', label: 'Aucun groupement' },
        ];
    }
  };

  const sortOptions = getSortOptions();
  const groupOptions = getGroupOptions();

  return (
    <div className={`flex flex-wrap items-center gap-4 p-3 bg-white border border-gray-200 rounded-md ${className}`}>
      {/* Tri */}
      <div className="flex items-center gap-2">
        <label className="text-sm font-medium text-gray-700">
          Trier par :
        </label>
        <select
          value={sortBy}
          onChange={(e) => onSortChange(e.target.value)}
          className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {sortOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {/* Groupement */}
      <div className="flex items-center gap-2">
        <label className="text-sm font-medium text-gray-700">
          Grouper par :
        </label>
        <select
          value={groupBy}
          onChange={(e) => onGroupChange(e.target.value)}
          className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {groupOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};

export default SortGroupControls;

